import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportError } from 'helpers/error-logging';

import { getCategoryRelationDelta, runSequentially } from './SkuCategoryRelations.helpers';
import { PARTNER_STATE } from './SkuItems.constants';
import {
  buildSkuItemAvailabilityUpdatePayload,
  linkItemToSubgroup,
  unlinkItemFromSubgroup,
  updateSkuGroup,
  updateSkuItem,
  updateSkuSubgroup,
} from './SkuItems.helpers';
import { skuMenuKeys } from './skuMenuKeys';

const sameId = (a, b) => String(a) === String(b);

const applyItemUpdate = (data, { itemId, updates }) => {
  if (!data) return data;
  return {
    ...data,
    items: data.items.map(item => (sameId(item.id, itemId) ? { ...item, ...updates } : item)),
  };
};

const stripCategoryFromItem = (item, categoryId) => {
  const currentSubGroupIds = item.subGroupIds || (item.subGroupId ? [item.subGroupId] : []);
  const subGroupIds = currentSubGroupIds.filter(id => !sameId(id, categoryId));
  const originalPrimaryRemoved = sameId(item.subGroupId, categoryId);
  const subGroupId = originalPrimaryRemoved ? subGroupIds[0] || null : item.subGroupId;
  return { ...item, subGroupIds, subGroupId };
};

const applyRemoveItemFromCategory = (data, { itemId, categoryId }) => {
  if (!data) return data;
  return {
    ...data,
    items: data.items.map(item => (sameId(item.id, itemId) ? stripCategoryFromItem(item, categoryId) : item)),
  };
};

const applyRemoveItemFromAllCategories = (data, { itemId }) => {
  if (!data) return data;
  return {
    ...data,
    items: data.items.map(item => (sameId(item.id, itemId) ? { ...item, subGroupIds: [], subGroupId: null } : item)),
  };
};

const applyDeleteItem = (data, { itemId }) => {
  if (!data) return data;
  return {
    ...data,
    items: data.items.filter(item => !sameId(item.id, itemId)),
  };
};

const applyDeleteCategory = (data, { categoryId }) => {
  if (!data) return data;
  return {
    ...data,
    categories: data.categories.filter(cat => !sameId(cat.id, categoryId)),
    items: data.items.map(item => stripCategoryFromItem(item, categoryId)),
  };
};

const applyCategoryUpdate = (data, { categoryId, updates }) => {
  if (!data) return data;
  return {
    ...data,
    categories: data.categories.map(cat => (sameId(cat.id, categoryId) ? { ...cat, ...updates } : cat)),
  };
};

const applyMenuUpdate = (menus, { menuId, updates }) => {
  if (!menus) return menus;
  return menus.map(menu => (sameId(menu.id, menuId) ? { ...menu, ...updates } : menu));
};

// cancelQueries first so an in-flight refetch can't land after our optimistic write
// and clobber it; invalidate on settle so the server, not the optimistic guess, wins.
const optimisticMutationOptions = ({ queryClient, getQueryKey, applyOptimistic, mutationFn, errorLabel }) => ({
  mutationFn,
  onMutate: async variables => {
    const queryKey = getQueryKey(variables);
    await queryClient.cancelQueries({ queryKey });
    const snapshot = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, current => applyOptimistic(current, variables));
    return { snapshot, queryKey };
  },
  onError: (error, variables, context) => {
    if (context) {
      queryClient.setQueryData(context.queryKey, context.snapshot);
    }
    reportError(errorLabel, error);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: skuMenuKeys.all });
  },
});

const useSkuMutations = ({ venueId }) => {
  const queryClient = useQueryClient();
  const detailsKey = () => skuMenuKeys.details(venueId);
  const menusKey = () => skuMenuKeys.menus(venueId);

  const statusChange = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { itemId, optimistic }) => applyItemUpdate(data, { itemId, updates: optimistic }),
      mutationFn: ({ itemId, payload }) => updateSkuItem(venueId, itemId, payload),
      errorLabel: 'Error changing SKU item availability',
    })
  );

  const removeFromCategory = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { itemId, categoryId }) => applyRemoveItemFromCategory(data, { itemId, categoryId }),
      mutationFn: ({ itemId, categoryId }) => unlinkItemFromSubgroup(venueId, categoryId, itemId),
      errorLabel: 'Error removing SKU item from category',
    })
  );

  const removeFromAllCategories = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { itemId }) => applyRemoveItemFromAllCategories(data, { itemId }),
      // allSettled, not all: Promise.all would reject on the first failure and abort
      // the sibling unlinks that are already in flight, leaving the server diverged.
      mutationFn: async ({ itemId, categoryIds }) => {
        const results = await Promise.allSettled(
          categoryIds.map(categoryId => unlinkItemFromSubgroup(venueId, categoryId, itemId))
        );
        const firstRejection = results.find(result => result.status === 'rejected');
        if (firstRejection) {
          throw firstRejection.reason;
        }
      },
      errorLabel: 'Error removing SKU item from all categories',
    })
  );

  const deleteItem = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { itemId }) => applyDeleteItem(data, { itemId }),
      mutationFn: ({ itemId, item }) =>
        updateSkuItem(
          venueId,
          itemId,
          buildSkuItemAvailabilityUpdatePayload(item, {
            enabled: false,
            soldOut: false,
            partnerState: PARTNER_STATE.DELETED,
          })
        ),
      errorLabel: 'Error deleting SKU item',
    })
  );

  const toggleCategory = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { categoryId, optimistic }) =>
        applyCategoryUpdate(data, { categoryId, updates: optimistic }),
      mutationFn: ({ categoryId, payload }) => updateSkuSubgroup(venueId, categoryId, payload),
      errorLabel: 'Error toggling SKU category',
    })
  );

  const updateCategory = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { categoryId, optimistic }) =>
        applyCategoryUpdate(data, { categoryId, updates: optimistic }),
      mutationFn: ({ categoryId, payload }) => updateSkuSubgroup(venueId, categoryId, payload),
      errorLabel: 'Error updating SKU category',
    })
  );

  const deleteCategory = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { categoryId }) => applyDeleteCategory(data, { categoryId }),
      // Unlink first, delete last: the category is only soft-deleted once every item has
      // been moved out, so a partial failure can never hide items inside a deleted
      // category (the backend performs no cascade and no validation of its own).
      mutationFn: async ({ categoryId, itemIds }) => {
        const unlinkedEntries = [];
        try {
          await runSequentially(itemIds, async itemId => {
            await unlinkItemFromSubgroup(venueId, categoryId, itemId);
            unlinkedEntries.push({ itemId, ordinal: unlinkedEntries.length });
          });
          await updateSkuSubgroup(venueId, categoryId, {
            enabled: false,
            partnerState: PARTNER_STATE.DELETED,
          });
        } catch (error) {
          // Best-effort compensation: re-link whatever was already unlinked so the
          // category is left intact. allSettled so one failed re-link doesn't abandon
          // the rest; the original error is what surfaces to the caller either way.
          await Promise.allSettled(
            unlinkedEntries.map(({ itemId, ordinal }) => linkItemToSubgroup(venueId, categoryId, itemId, ordinal))
          );
          throw error;
        }
      },
      errorLabel: 'Error deleting SKU category',
    })
  );

  const updateMenu = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: menusKey,
      applyOptimistic: (menus, { menuId, optimistic }) => applyMenuUpdate(menus, { menuId, updates: optimistic }),
      mutationFn: ({ menuId, payload }) => updateSkuGroup(venueId, menuId, payload),
      errorLabel: 'Error updating SKU menu',
    })
  );

  const editItem = useMutation(
    optimisticMutationOptions({
      queryClient,
      getQueryKey: detailsKey,
      applyOptimistic: (data, { itemId, optimistic }) => applyItemUpdate(data, { itemId, updates: optimistic }),
      mutationFn: async ({ itemId, payload, baseCategoryIds, nextCategories }) => {
        await updateSkuItem(venueId, itemId, payload);
        const { removedCategoryIds, addedCategoryIds } = getCategoryRelationDelta({
          previousCategoryIds: baseCategoryIds,
          nextCategories,
        });
        await runSequentially(removedCategoryIds, subgroupId => unlinkItemFromSubgroup(venueId, subgroupId, itemId));
        const addedSubgroupEntries = addedCategoryIds.map((subgroupId, ordinal) => ({ subgroupId, ordinal }));
        await runSequentially(addedSubgroupEntries, ({ subgroupId, ordinal }) =>
          linkItemToSubgroup(venueId, subgroupId, itemId, ordinal)
        );
      },
      errorLabel: 'Error editing SKU item',
    })
  );

  return {
    statusChange,
    removeFromCategory,
    removeFromAllCategories,
    deleteItem,
    toggleCategory,
    updateCategory,
    deleteCategory,
    updateMenu,
    editItem,
  };
};

export default useSkuMutations;
